/* buy-now-link.js
 * Smooth-scroll behavior for the site-wide "Buy now" link.
 *
 * The link rendered by `snippets/buy-now-link.liquid` always points at
 * `<product-url>#buy`. The `#buy` anchor lives just above the variant picker
 * inside `sections/main-product.liquid`.
 *
 * Approach: when the page is loaded with `#buy` in the URL, we:
 *   1. Disable browser scroll-restoration so a refresh starts at the top.
 *   2. Immediately reset to the top so the visitor never sees the instant
 *      browser-default jump.
 *   3. Wait for `window.onload` (all images / fonts / async content
 *      finished) so the buy section's real position has settled.
 *   4. Call `scrollIntoView({ behavior: 'smooth', block: 'start' })` —
 *      which respects the `scroll-margin-top` on `.buy-anchor` and
 *      gives us a clean header offset without us having to measure it.
 *
 * Same-page clicks on `<a href="#buy">` (or any link whose final hash
 * resolves to #buy on the current page) are intercepted and smooth-scrolled
 * the same way.
 */
(function () {
  'use strict';

  // Loaded marker — run `window.__buyNowLinkLoaded` in the console to
  // confirm this script is on the page.
  window.__buyNowLinkLoaded = '2026-05-13-release';

  var TARGET_ID = 'buy';
  var TARGET_HASH = '#' + TARGET_ID;
  // Set DEBUG = true or `window.__buyNowDebug = true` in the console to
  // see verbose `[buy-now-link]` logs while testing.
  var DEBUG = false;

  function log() {
    if (!(DEBUG || window.__buyNowDebug)) return;
    var args = ['[buy-now-link]'].concat([].slice.call(arguments));
    console.log.apply(console, args);
  }

  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  // Read scroll-margin-top from the .buy-anchor element if it's set in
  // CSS, otherwise fall back to a sensible default. This keeps the
  // header offset consistent between scrollIntoView and manual scrollTo.
  function getScrollMarginTop(target) {
    var computed = window.getComputedStyle(target);
    var raw = computed.scrollMarginTop || computed.getPropertyValue('scroll-margin-top') || '';
    var px = parseFloat(raw);
    if (isNaN(px) || px <= 0) {
      // Default: ~10rem desktop, ~7rem mobile (matching buy-now-link.css)
      return window.innerWidth < 750 ? 112 : 160;
    }
    return px;
  }

  // Walk the offsetParent chain to compute the element's natural document
  // position. Unlike getBoundingClientRect().top + pageYOffset, this is
  // immune to CSS transforms and to position: sticky displacement — the
  // reported value is always where the element sits in the document
  // layout, regardless of where it's currently being displayed.
  function getNaturalDocumentTop(el) {
    var top = 0;
    var node = el;
    while (node) {
      top += node.offsetTop || 0;
      node = node.offsetParent;
    }
    return top;
  }

  function smoothScrollToBuy() {
    var target = document.getElementById(TARGET_ID);
    if (!target) {
      log('target #buy not found on this page');
      return false;
    }
    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var anchorTop = getNaturalDocumentTop(target);
    var rectTop = target.getBoundingClientRect().top + window.pageYOffset;
    var marginTop = getScrollMarginTop(target);
    var destination = Math.max(0, anchorTop - marginTop);
    log('scrolling to #buy', { reduced: prefersReduced, anchorTop_natural: anchorTop, anchorTop_rect: rectTop, marginTop: marginTop, destination: destination, currentScroll: window.pageYOffset });

    // Use window.scrollTo with an explicit destination — this is more
    // reliable than scrollIntoView, which can be silently ignored or
    // interrupted by other scroll handlers on the page.
    try {
      window.scrollTo({
        top: destination,
        left: 0,
        behavior: prefersReduced ? 'auto' : 'smooth'
      });
    } catch (e) {
      // Older browsers that don't accept the options object.
      window.scrollTo(0, destination);
    }

    if (typeof target.focus === 'function') {
      try { target.focus({ preventScroll: true }); } catch (e) { /* older browsers */ }
    }

    // Verify the scroll actually moved the page. If it didn't (e.g.
    // because a smooth scroll was cancelled), retry with an instant
    // jump after a short delay.
    window.setTimeout(function () {
      var diff = Math.abs(window.pageYOffset - destination);
      if (diff > 50) {
        log('scroll did not reach destination (diff=' + diff + 'px), retrying with instant scroll');
        window.scrollTo(0, destination);
      } else {
        log('scroll complete, final position=' + window.pageYOffset);
      }
    }, 900);

    return true;
  }

  // 1) ARRIVAL: page loaded with #buy in the URL.
  if (window.location.hash === TARGET_HASH) {
    log('arrival with #buy hash, current scroll=' + window.pageYOffset);
    // Reset to top so the visitor doesn't see the default instant jump.
    // We do this on every chance we get because the browser may try to
    // re-apply the hash scroll after our deferred script runs.
    window.scrollTo(0, 0);

    // After all images / fonts / async sections have settled, smooth-scroll.
    // We use window.load (not DOMContentLoaded) because hero / product
    // gallery images often cause significant layout shift below the fold.
    function onReady() {
      // One more reset to top in case anything scrolled us back during load.
      window.scrollTo(0, 0);
      // Give the browser one frame to paint the top-of-page state, then go.
      window.requestAnimationFrame(function () {
        window.setTimeout(smoothScrollToBuy, 60);
      });
    }

    if (document.readyState === 'complete') {
      onReady();
    } else {
      window.addEventListener('load', onReady, { once: true });
    }
  }

  // 2) CLICK: intercept clicks on any link whose final URL is the
  // current page with #buy. Cross-page clicks fall through to normal
  // navigation; the arrival handler above takes over on the next page.
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return; // left-click only
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var anchor = event.target.closest && event.target.closest('a[href]');
    if (!anchor) return;

    var href = anchor.getAttribute('href') || '';
    var url;
    try {
      url = new URL(href, window.location.href);
    } catch (e) {
      return;
    }
    if (url.hash !== TARGET_HASH) return;
    if (url.pathname !== window.location.pathname || url.host !== window.location.host) return;
    if (!document.getElementById(TARGET_ID)) return;

    event.preventDefault();
    if (history.replaceState) {
      history.replaceState(null, '', TARGET_HASH);
    }
    smoothScrollToBuy();
  });

  // 3) HASHCHANGE: in-page hash updates (e.g. someone types the URL
  // in the address bar and hits Enter while already on the page).
  window.addEventListener('hashchange', function () {
    log('hashchange fired, new hash=' + window.location.hash);
    if (window.location.hash === TARGET_HASH) smoothScrollToBuy();
  });

  // 4) PUBLIC MANUAL TRIGGER for debugging / future use.
  // Run `window.buyNowScroll()` in the console to force a scroll
  // regardless of current hash state.
  window.buyNowScroll = smoothScrollToBuy;
})();
